<template>
  <PageContentLayoutOnly :aria-label="$t('unitgroup.title')" role="region">
    <b-button
      add-unitgroup-button
      type="is-primary"
      class="is-rounded"
      @click="openAddUnitGroupModal()"
      :style="{ marginBottom: '20px' }"
      >Add Unit Group</b-button
    >
    <ModernTable
      active-table
      headerColor="#fff"
      parentRowColor="#F1FBFE"
      :expandableRows="true"
      :columns="columns"
      :rows="rows"
      :region="region"
      :filters="filters"
      :pagination="{
        perPage: 30
      }"
      :headerZIndex="10"
      condensed
      :useLoader="false"
    />

    <Modal :toggle.sync="showUnitGroupModal" unit-group-modal>
      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <transition name="fade" mode="out-in" appear v-if="action == 'update' || action == 'add'">
          <form @submit.prevent="handleSubmit(onFormSubmit)" class="form">
            <fieldset>
              <valid-input
                type="hidden"
                vid="unitGroupID"
                v-model="formData.unitGroupID"
                :style="{ visibility: 'hidden', position: 'absolute' }"
              />
              <div class="column is-12">
                <valid-input
                  style="width:50px !important;"
                  name="Name"
                  label="Name"
                  maxlength="100"
                  type="text"
                  vid="Name"
                  placeholder="Name"
                  spellcheck="true"
                  aria-label="Name"
                  rules="max:100|required"
                  class="is-small"
                  v-model="formData.Name"
                />
              </div>

              <div class="column is-12">
                <button
                  add
                  v-if="action && action == 'add'"
                  type="submit"
                  :disabled="loading"
                  class="button is-primary is-rounded"
                  :style="{ float: 'right' }"
                >
                  Add
                </button>
                <button
                  update
                  v-if="action && action == 'update'"
                  @click.prevent="updateUnitGroup(selectedUnitGroup, selectedID)"
                  class="button is-primary is-rounded"
                  :style="{ float: 'right' }"
                >
                  Update
                </button>
              </div>
            </fieldset>
            <fieldset>
              <ValidationProvider vid="providerErrors" v-slot="{ errors }">
                <div class="field">
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </fieldset>
          </form>
        </transition>
      </ValidationObserver>
    </Modal>

    <Modal :toggle.sync="showUnitGroupUnitModal">
      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <transition
          name="fade"
          mode="out-in"
          appear
          v-if="action == 'update' || action == 'addunitgroupunit'"
        >
          <form @submit.prevent="handleSubmit(addUnitGroup)" class="form">
            <fieldset>
              <label class="title is-7 tooltip-label">{{ formData.Name }}</label>
              <valid-input type="hidden" vid="unitGroupUnitID" v-model="formData.UnitGroupUnitID" />
              <valid-input
                type="hidden"
                vid="unitGroupID"
                v-model="formData.UnitGroupID"
                :style="{ position: 'absolute', visibility: 'hidden' }"
              />
              <div message-modal>
                <div class="pb-1 is-size-4" v-if="unitList">
                  Select Property
                </div>
                <div class="columns box is-12">
                  <div class="box list-content column is-12">
                    <b-field label="Search">
                      <b-input @input="filterList" v-model="filterBy" />
                    </b-field>
                    <b-table
                      :data="unitList"
                      :loading="loading"
                      :striped="true"
                      :sticky-header="true"
                      :checked-rows.sync="checkedRows"
                      checkable
                      :checkbox-position="checkboxPosition"
                      :style="{ maxHeight: '400px' }"
                    >
                      <template slot="empty">
                        <empty-list
                          v-if="!unitList || (unitList !== undefined && unitList.length === 0)"
                        />
                      </template>
                      <b-table-column
                        label="Property Address"
                        field="address"
                        sortable
                        v-slot="props"
                        ><span v-if="props.row.address !== undefined">{{
                          `${props.row.address.addressOne || ''} ${props.row.address.city ||
                            ''} ${props.row.address.state || ''}, ${props.row.address.postalCode ||
                            ''}` || ''
                        }}</span>
                        <span v-else>&nbsp;</span>
                      </b-table-column>
                    </b-table>
                  </div>
                </div>
              </div>
              <div class="column is-12">
                <button
                  v-if="action && action == 'addunitgroupunit'"
                  type="button"
                  :disabled="loading"
                  class="button is-primary is-rounded float-right"
                  @click.prevent="addUnitGroupUnit"
                >
                  Add
                </button>
                <button
                  v-if="action && action == 'updateunitgroupunit'"
                  @click.prevent="updateUnitGroupUnit(selectedUnitGroup, selectedID)"
                  class="button is-primary is-rounded float-right"
                >
                  Update
                </button>
              </div>
            </fieldset>
            <fieldset>
              <ValidationProvider vid="providerErrors" v-slot="{ errors }">
                <div class="field">
                  <span>{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </fieldset>
          </form>
        </transition>
      </ValidationObserver>
    </Modal>
  </PageContentLayoutOnly>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapState } from 'vuex'

// components
import ValidInput from '@/components/inputs/ValidInput'
import ModernTable from '@/components/tables/Modern/Table'
import Modal from '@/components/Modal'
import PageContentLayoutOnly from '@/containers/PageContentLayoutOnly'
import kms from '@/services/kms'
import { data } from './keys/data'
import { methods } from './keys/methods'
import { mounted } from './keys/mounted'
import { watch } from './keys/watch'
import EmptyList from '@/components/EmptyList'

export default {
  components: {
    Modal,
    ModernTable,
    PageContentLayoutOnly,
    ValidInput,
    ValidationObserver,
    ValidationProvider,
    EmptyList
  },

  props: {
    // for the purpose of testing
    kms: {
      type: Object,
      default: () => {
        return kms
      }
    },
    unitTestID: {
      type: Number,
      default: null
    }
  },

  data,
  watch,
  methods,
  mounted,

  created() {
    this.getRefreshedUnitGroups()
  },

  computed: {
    ...mapState({
      hoaId: state => state.user.selectedHoaId
    })
  },

  i18n: {
    messages: {
      en: { unitgroup: { title: 'Unit Groups' } }
    }
  }
}
</script>
